import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  School,
  Science,
  Article,
  Code,
  Email,
  LinkedIn,
  GitHub,
} from '@mui/icons-material';
import profileConfig from '../constants/profileConfig';
import { FaLinkedin, FaResearchgate, FaEnvelope } from 'react-icons/fa';
import { SiGooglescholar } from 'react-icons/si';

const Home = () => {
  const navigate = useNavigate();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  const quickLinks = [
    {
      title: 'About Me',
      description: 'Learn about my background and expertise',
      icon: <School />,
      path: '/about',
      color: '#1976d2',
    },
    {
      title: 'Current Research',
      description: 'Explore my ongoing research projects',
      icon: <Science />,
      path: '/research',
      color: '#dc004e',
    },
    {
      title: 'Publications',
      description: 'Browse my published papers and research',
      icon: <Article />,
      path: '/publications',
      color: '#2e7d32',
    },
    {
      title: 'Projects',
      description: 'View my completed projects and outcomes',
      icon: <Code />,
      path: '/projects',
      color: '#ed6c02',
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: 8,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <motion.div variants={itemVariants}>
                  <Typography
                    variant="h1"
                    sx={{
                      fontWeight: 700,
                      mb: 2,
                      fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
                    }}
                  >
                    {profileConfig.fullName || profileConfig.displayName || ''}
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      mb: 3,
                      opacity: 0.9,
                      fontWeight: 300,
                      fontSize: { xs: '1.1rem', sm: '1.5rem', md: '2rem' },
                    }}
                  >
                    {profileConfig.title || ''}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 4,
                      opacity: 0.8,
                      lineHeight: 1.6,
                      fontSize: { xs: '1rem', sm: '1.2rem', md: '1.3rem' },
                    }}
                  >
                    {(profileConfig.location || '') + (profileConfig.organization ? ' • ' + profileConfig.organization : '')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <Button
                        variant="contained"
                        size="large"
                        onClick={() => navigate('/contact')}
                        sx={{
                          backgroundColor: 'white',
                          color: 'primary.main',
                          '&:hover': {
                            backgroundColor: 'grey.100',
                          },
                          width: { xs: '100%', sm: 'auto' },
                        }}
                      >
                        Get In Touch
                      </Button>
                    </motion.div>
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <Button
                        variant="outlined"
                        size="large"
                        onClick={() => navigate('/research')}
                        sx={{
                          borderColor: 'white',
                          color: 'white',
                          '&:hover': {
                            borderColor: 'white',
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          },
                          width: { xs: '100%', sm: 'auto' },
                        }}
                      >
                        View Research
                      </Button>
                    </motion.div>
                  </Box>
                </motion.div>
              </Grid>
              <Grid item xs={12} md={6}>
                <motion.div
                  variants={itemVariants}
                  style={{ display: 'flex', justifyContent: 'center' }}
                >
                  <Avatar
                    src={profileConfig.profileImage}
                    alt={profileConfig.name}
                    sx={{
                      width: { xs: 140, sm: 200, md: 300 },
                      height: { xs: 140, sm: 200, md: 300 },
                      border: '4px solid white',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                    }}
                  />
                </motion.div>
              </Grid>
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* Quick Links Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, sm: 8 } }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Typography
            variant="h2"
            align="center"
            sx={{ mb: { xs: 3, sm: 6 }, fontWeight: 600, fontSize: { xs: '1.5rem', sm: '2.2rem', md: '2.8rem' } }}
          >
            Explore My Work
          </Typography>
          <Grid container spacing={4}>
            {quickLinks.map((link, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <motion.div variants={itemVariants}>
                  <Card
                    sx={{
                      height: '100%',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                      },
                      transition: 'transform 0.3s ease',
                      p: { xs: 1, sm: 3 },
                    }}
                    onClick={() => navigate(link.path)}
                  >
                    <CardContent
                      sx={{
                        textAlign: 'center',
                        p: 0,
                      }}
                    >
                      <Box
                        sx={{
                          width: { xs: 40, sm: 60 },
                          height: { xs: 40, sm: 60 },
                          borderRadius: '50%',
                          backgroundColor: link.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto 16px',
                          color: 'white',
                          fontSize: { xs: '1.2rem', sm: '1.5rem' },
                        }}
                      >
                        {link.icon}
                      </Box>
                      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                        {link.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                        {link.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </Container>

      {/* Call to Action Section */}
      <Box
        sx={{
          backgroundColor: 'primary.main',
          color: 'white',
          py: 8,
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <Typography
              variant="h2"
              align="center"
              sx={{ mb: 3, fontWeight: 600 }}
            >
              {profileConfig.callToAction.title}
            </Typography>
            <Typography
              variant="h6"
              align="center"
              sx={{ mb: 4, opacity: 0.9 }}
            >
              {profileConfig.callToAction.subtitle}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
              <Grid container spacing={2} maxWidth="md">
                {profileConfig.callToAction.opportunities.map((opportunity, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Chip
                      label={opportunity}
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.3)',
                        },
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Email />}
                  onClick={() => navigate('/contact')}
                  sx={{
                    backgroundColor: 'white',
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'grey.100',
                    },
                  }}
                >
                  Contact Me
                </Button>
              </motion.div>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Social Links */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
          {(profileConfig.socialLinks || []).map((link, idx) => (
            <motion.div key={link.type || idx} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
              <Button
                component="a"
                href={link.url || '#'}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                size="small"
                sx={{ borderRadius: '50%', minWidth: 48, height: 48, p: 0 }}
              >
                {link.type === 'linkedin' && <FaLinkedin />}
                {link.type === 'researchgate' && <FaResearchgate />}
                {link.type === 'googleScholar' && <SiGooglescholar />}
                {link.type === 'email' && <FaEnvelope />}
              </Button>
            </motion.div>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default Home; 